import { saveGameResult } from '@db/services/stats.service';
import { isAuthRelated } from '@db/utils/errors';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import type { SavePayload } from '@/store/gameStore';

const SAVE_TIMEOUT_MS = 3000;
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 500;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

const createSaveTimeout = (ms: number): Promise<never> =>
  new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Save timeout')), ms);
  });

const saveScoreToSupabase = async (
  userId: string,
  finalScore: number,
  blockCounts: Record<string, number>,
): Promise<void> => {
  if (!supabase) throw new Error('Supabase not initialized');
  await saveGameResult(supabase, userId, finalScore, blockCounts);
};

export const saveScore = async (payload: SavePayload): Promise<boolean> => {
  if (!navigator.onLine) return false;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const userId = await useAuthStore.getState().ensureValidUserId();
    if (!userId) return false;

    try {
      await Promise.race([
        saveScoreToSupabase(userId, payload.finalScore, payload.blockCounts),
        createSaveTimeout(SAVE_TIMEOUT_MS),
      ]);
      return true;
    } catch (error) {
      const lastAttempt = attempt === MAX_RETRIES - 1;

      if (!lastAttempt && isAuthRelated(error)) {
        await useAuthStore.getState().refreshSession();
        await delay(RETRY_BASE_MS * 2 ** attempt);
        continue;
      }

      if (lastAttempt) {
        Sentry.captureException(error, {
          tags: { feature: 'score_save' },
          extra: {
            finalScore: payload.finalScore,
            blockCounts: payload.blockCounts,
            attempts: attempt + 1,
          },
        });
      }
    }
  }
  return false;
};
